import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  ISubdivision,
  ISubdivisionAddress,
  ICreateSubdivisionPayload,
  IDeleteSubdivisionPayload,
  ISubdivisionItem,
  ISubdivisionListParams,
  ISubdivisionParams,
  IUpdateSubdivisionPayload,
} from 'entities/Subdivision/Subdivision.models';
import { subdivisionTransport } from 'entities/Subdivision/Subdivision.transport';
import { IUser, IUserListParams } from 'entities/User/User.models';
import { userTransport } from 'entities/User/User.transport';
import { patchCurrentUserSubdivision } from 'entities/User/User.slice';
import { IAddAddressSubdivisionPayload, IRemoveAddressSubdivisionPayload } from 'entities/Address/Address.models';
import { addressesTransport } from 'entities/Address/Address.transport';

interface IState {
  subdivision: ISubdivision | null;
  subdivisionList: ISubdivisionItem[];
  subdivisionCount: number;
  isSubdivisionLoading: boolean;
  subdivisionUsers: IUser[];
  subdivisionUserCount: number;
}

const initialState: IState = {
  subdivision: null,
  subdivisionList: [],
  subdivisionCount: 0,
  isSubdivisionLoading: false,
  subdivisionUsers: [],
  subdivisionUserCount: 0,
};

export const subdivisionSlice = createSlice({
  name: 'subdivision',
  initialState,
  reducers: {
    setSubdivision(state, action: PayloadAction<ISubdivision | null>) {
      state.subdivision = action.payload;
    },
    setSubdivisionList(state, action: PayloadAction<ISubdivisionItem[]>) {
      state.subdivisionList = action.payload;
    },
    setSubdivisionCount(state, action: PayloadAction<number>) {
      state.subdivisionCount = action.payload;
    },
    setIsSubdivisionLoading(state, action: PayloadAction<boolean>) {
      state.isSubdivisionLoading = action.payload;
    },
    setSubdivisionUsers(state, action: PayloadAction<IUser[]>) {
      state.subdivisionUsers = action.payload;
    },
    addSubdivisionUsersChunk(state, action: PayloadAction<IUser[]>) {
      state.subdivisionUsers.push(...action.payload);
    },
    setSubdivisionUserCount(state, action: PayloadAction<number>) {
      state.subdivisionUserCount = action.payload;
    },
    addSubdivisionListItem(state, action: PayloadAction<ISubdivision>) {
      state.subdivisionList.push(action.payload);
    },
    patchSubdivisionListItem(state, action: PayloadAction<ISubdivision>) {
      const item = state.subdivisionList.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeSubdivisionListItem(state, action: PayloadAction<number>) {
      state.subdivisionList = state.subdivisionList.filter((item) => item.id !== action.payload);
    },
    addSubdivisionUsersItem(state, action: PayloadAction<IUser>) {
      state.subdivisionUsers.push(action.payload);
      state.subdivisionUserCount += 1;
    },
    removeSubdivisionUsersItem(state, action: PayloadAction<number>) {
      state.subdivisionUsers = state.subdivisionUsers.filter((item) => item.id !== action.payload);
      state.subdivisionUserCount -= 1;
    },
    addSubdivisionAddressItem(state, action: PayloadAction<ISubdivisionAddress>) {
      if (!state.subdivision) {
        return;
      }

      state.subdivision.addresses.push(action.payload);
    },
    patchSubdivisionAddressItem(state, action: PayloadAction<number>) {
      if (!state.subdivision) {
        return;
      }

      state.subdivision.addresses = state.subdivision.addresses.map((item) => {
        if (item.id === action.payload) {
          return {
            ...item,
            isMain: true,
          };
        }

        return {
          ...item,
          isMain: false,
        };
      });
    },
    removeSubdivisionAddressItem(state, action: PayloadAction<number>) {
      if (!state.subdivision) {
        return;
      }

      state.subdivision.addresses.filter((item) => item.id !== action.payload);
    },
    filterSubdivisionList(state, action: PayloadAction<number[]>) {
      state.subdivisionList = state.subdivisionList.filter((item) => !action.payload.includes(item.id));
    },
  },
});

export const {
  setSubdivision,
  setSubdivisionList,
  setSubdivisionCount,
  setIsSubdivisionLoading,
  setSubdivisionUsers,
  addSubdivisionUsersChunk,
  setSubdivisionUserCount,
  addSubdivisionListItem,
  patchSubdivisionListItem,
  removeSubdivisionListItem,
  addSubdivisionUsersItem,
  removeSubdivisionUsersItem,
  addSubdivisionAddressItem,
  patchSubdivisionAddressItem,
  removeSubdivisionAddressItem,
  filterSubdivisionList,
} = subdivisionSlice.actions;

export const selectSubdivision = (state: RootState) => state.subdivision.subdivision;
export const selectSubdivisionList = (state: RootState) => state.subdivision.subdivisionList;
export const selectSubdivisionCount = (state: RootState) => state.subdivision.subdivisionCount;
export const selectIsSubdivisionLoading = (state: RootState) => state.subdivision.isSubdivisionLoading;
export const selectSubdivisionUsers = (state: RootState) => state.subdivision.subdivisionUsers;
export const selectSubdivisionUserCount = (state: RootState) => state.subdivision.subdivisionUserCount;

export const subdivisionReducer = subdivisionSlice.reducer;

export const getSubdivisionList = (params: ISubdivisionListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await subdivisionTransport.get(params);
    dispatch(setSubdivisionList(response.data));
    dispatch(setSubdivisionCount(response.count));
    return response.data;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const getSubdivisionById = (params: ISubdivisionParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await subdivisionTransport.getById(params);
    dispatch(setSubdivision(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const createSubdivision = (payload: ICreateSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await subdivisionTransport.create(payload);
    dispatch(addSubdivisionListItem(response));
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const updateSubdivision = (payload: IUpdateSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await subdivisionTransport.update(payload);
    dispatch(patchSubdivisionListItem(response));
    dispatch(setSubdivision(response));
    dispatch(patchCurrentUserSubdivision(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const deleteSubdivision = (payload: IDeleteSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await subdivisionTransport.delete(payload);
    dispatch(removeSubdivisionListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const getSubdivisionUsers = (params: IUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await userTransport.get(params);
    dispatch(setSubdivisionUsers(response.data));
    dispatch(setSubdivisionUserCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const getSubdivisionUsersChunk = (params: IUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await userTransport.get(params);
    dispatch(addSubdivisionUsersChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const addSubdivisionAddress = (payload: IAddAddressSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await addressesTransport.addSubdivision(payload);
    dispatch(addSubdivisionAddressItem(response as ISubdivisionAddress));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const updateSubdivisionAddress = (payload: IAddAddressSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await addressesTransport.addSubdivision(payload);
    dispatch(patchSubdivisionAddressItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};

export const deleteSubdivisionAddress = (payload: IRemoveAddressSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSubdivisionLoading(true));

  try {
    const response = await addressesTransport.removeSubdivision(payload);
    dispatch(removeSubdivisionAddressItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSubdivisionLoading(false));
  }
};
